// LuxeSalonLayout.js - Premium Salon Theme
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StoreLogo, CartBtn, TabContent, StoreFooter, 
  AWS_URL, FaHeart
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-gold-200 bg-white/90 px-3 py-2 text-sm text-gray-900 outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-100 dark:border-gold-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  const displayValue = p.storeTheme?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function LuxeSalonLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#8B4513";
  const secondary = T.secondaryColor || "#d4af37";
  const gold = "#d4af37";
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedStylist, setSelectedStylist] = useState(null);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [animationComplete, setAnimationComplete] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [bookingDate, setBookingDate] = useState("");
  
  const heroRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);

  // Salon hero images
  const salonHeroImages = [
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/f5d265fc-1b77-4545-a8d6-9773423763d6_23.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b4d7f739-8fd0-42cd-b95c-b53b7fcce9ee_24.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/3935bfb8-cfc7-449e-bfe1-b26efe6e7b5d_25.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/50d6df4d-8a41-42f7-af23-657524141756_26.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/cb0407bd-baed-47fb-9e74-3504897464db_27.jpg",
  ];

  // Salon Services
  const salonServices = [
    // Hair Services
    {
      id: 1,
      nameKey: "service1_name",
      name: "Luxury Hair Cut & Styling",
      priceKey: "service1_price",
      price: 2499,
      originalPriceKey: "service1_original_price",
      originalPrice: 3499,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/f5d265fc-1b77-4545-a8d6-9773423763d6_23.jpg",
      categoryKey: "service1_category",
      category: "Hair",
      subCategory: "Cut & Styling",
      descriptionKey: "service1_description",
      description: "Expert hair cutting and styling with premium products for a flawless look.",
      badge: "Signature",
      badgeColor: "#d4af37",
      duration: "60 min",
      level: "Master Stylist",
      benefits: ["Personalized consultation", "Premium products", "Blow-dry included"],
      inStock: true,
    },
    {
      id: 2,
      nameKey: "service2_name",
      name: "Royal Hair Color",
      priceKey: "service2_price",
      price: 4999,
      originalPriceKey: "service2_original_price",
      originalPrice: 6999,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b4d7f739-8fd0-42cd-b95c-b53b7fcce9ee_24.jpg",
      categoryKey: "service2_category",
      category: "Hair",
      subCategory: "Color",
      descriptionKey: "service2_description",
      description: "Premium hair coloring with the finest professional products and expert technique.",
      badge: "Premium",
      badgeColor: "#8B4513",
      duration: "120 min",
      level: "Color Specialist",
      benefits: ["Consultation", "Premium color", "Treatment included"],
      inStock: true,
    },
    {
      id: 3,
      nameKey: "service3_name",
      name: "Keratin Smoothing Treatment",
      priceKey: "service3_price",
      price: 6999,
      originalPriceKey: "service3_original_price",
      originalPrice: 8999,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/3935bfb8-cfc7-449e-bfe1-b26efe6e7b5d_25.jpg",
      categoryKey: "service3_category",
      category: "Hair",
      subCategory: "Treatment",
      descriptionKey: "service3_description",
      description: "Luxurious keratin treatment for smooth, frizz-free hair that shines.",
      badge: "Bestseller",
      badgeColor: "#d4af37",
      duration: "150 min",
      level: "Treatment Expert",
      benefits: ["Smooth results", "Long-lasting", "Frizz control"],
      inStock: true,
    },
    {
      id: 4,
      nameKey: "service4_name",
      name: "Bridal Hair Package",
      priceKey: "service4_price",
      price: 9999,
      originalPriceKey: "service4_original_price",
      originalPrice: 13999,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/50d6df4d-8a41-42f7-af23-657524141756_26.jpg",
      categoryKey: "service4_category",
      category: "Hair",
      subCategory: "Bridal",
      descriptionKey: "service4_description",
      description: "Complete bridal hair package including trial, styling, and touch-up service.",
      badge: "Luxury",
      badgeColor: "#d4af37",
      duration: "180 min",
      level: "Bridal Specialist",
      benefits: ["Trial session", "Wedding day styling", "Touch-up service"],
      inStock: true,
    },
    
    // Skin Services
    {
      id: 5,
      nameKey: "service5_name",
      name: "Luxury Facial",
      priceKey: "service5_price",
      price: 3999,
      originalPriceKey: "service5_original_price",
      originalPrice: 5499,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/6e0a903c-5c91-4883-8989-5a567be67e68_makeup.webp",
      categoryKey: "service5_category",
      category: "Skin",
      subCategory: "Facials",
      descriptionKey: "service5_description",
      description: "Rejuvenating facial using premium products for radiant, glowing skin.",
      badge: "Popular",
      badgeColor: "#8B4513",
      duration: "75 min",
      level: "Skin Specialist",
      benefits: ["Deep cleansing", "Exfoliation", "Customized mask"],
      inStock: true,
    },
    {
      id: 6,
      nameKey: "service6_name",
      name: "Gold Leaf Facial",
      priceKey: "service6_price",
      price: 7999,
      originalPriceKey: "service6_original_price",
      originalPrice: 10999,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b95b8294-38e8-47c7-b8ea-c208bbf9df65_28.jpg",
      categoryKey: "service6_category",
      category: "Skin",
      subCategory: "Luxury Facials",
      descriptionKey: "service6_description",
      description: "Ultimate luxury facial with 24k gold leaf for supreme anti-aging benefits.",
      badge: "Signature",
      badgeColor: "#d4af37",
      duration: "90 min",
      level: "Master Esthetician",
      benefits: ["24k gold infusion", "Anti-aging", "Lymphatic drainage"],
      inStock: true,
    },
    {
      id: 7,
      nameKey: "service7_name",
      name: "Bridal Skin Glow",
      priceKey: "service7_price",
      price: 5999,
      originalPriceKey: "service7_original_price",
      originalPrice: 7999,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/3b64aa13-b428-40e7-a7fa-09416d4e3f48_29.jpg",
      categoryKey: "service7_category",
      category: "Skin",
      subCategory: "Bridal",
      descriptionKey: "service7_description",
      description: "Comprehensive bridal skin package for that perfect wedding day glow.",
      badge: "Bridal",
      badgeColor: "#d4af37",
      duration: "120 min",
      level: "Bridal Skin Expert",
      benefits: ["Customized routine", "Pre-wedding glow", "Treatment plan"],
      inStock: true,
    },
    
    // Nail Services
    {
      id: 8,
      nameKey: "service8_name",
      name: "Luxury Manicure",
      priceKey: "service8_price",
      price: 1499,
      originalPriceKey: "service8_original_price",
      originalPrice: 1999,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/4dff4141-d7c0-479d-93ea-6d85ff0ce84e_30.jpg",
      categoryKey: "service8_category",
      category: "Nails",
      subCategory: "Manicure",
      descriptionKey: "service8_description",
      description: "Premium manicure with paraffin treatment and expert nail care.",
      badge: "Popular",
      badgeColor: "#8B4513",
      duration: "45 min",
      level: "Nail Specialist",
      benefits: ["Paraffin treatment", "Cuticle care", "Long-lasting polish"],
      inStock: true,
    },
    {
      id: 9,
      nameKey: "service9_name",
      name: "Luxury Pedicure",
      priceKey: "service9_price",
      price: 1999,
      originalPriceKey: "service9_original_price",
      originalPrice: 2699,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/eb2a51a6-db97-4955-864b-a690f703d783_pedicure.jpeg",
      categoryKey: "service9_category",
      category: "Nails",
      subCategory: "Pedicure",
      descriptionKey: "service9_description",
      description: "Complete pedicure with foot massage and premium products.",
      badge: "Relaxing",
      badgeColor: "#8B4513",
      duration: "60 min",
      level: "Nail Specialist",
      benefits: ["Foot massage", "Exfoliation", "Hydration treatment"],
      inStock: true,
    },
    
    // Makeup Services
    {
      id: 10,
      nameKey: "service10_name",
      name: "Bridal Makeup",
      priceKey: "service10_price",
      price: 8999,
      originalPriceKey: "service10_original_price",
      originalPrice: 11999,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/0ade75e8-cf41-4cf3-8d04-2621e95f1431_bridal.jpg",
      categoryKey: "service10_category",
      category: "Makeup",
      subCategory: "Bridal",
      descriptionKey: "service10_description",
      description: "Complete bridal makeup with premium products and trial session.",
      badge: "Luxury",
      badgeColor: "#d4af37",
      duration: "120 min",
      level: "Master Makeup Artist",
      benefits: ["Trial session", "Premium products", "Touch-up kit"],
      inStock: true,
    },
    {
      id: 11,
      nameKey: "service11_name",
      name: "Party Makeup",
      priceKey: "service11_price",
      price: 3999,
      originalPriceKey: "service11_original_price",
      originalPrice: 5499,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/e52f502a-d4d2-4558-a9ba-db48a01df548_33.jpg",
      categoryKey: "service11_category",
      category: "Makeup",
      subCategory: "Event",
      descriptionKey: "service11_description",
      description: "Stunning party makeup for special occasions and events.",
      badge: "Glow Up",
      badgeColor: "#8B4513",
      duration: "90 min",
      level: "Makeup Artist",
      benefits: ["Customized look", "Premium products", "Setting spray"],
      inStock: true,
    },
  ];

  // Categories
  const serviceCategories = [
    { key: "all", icon: "💇‍♀️", label: "All Services", color: "#d4af37" },
    { key: "hair", icon: "💇‍♀️", label: "Hair", color: "#8B4513" },
    { key: "skin", icon: "✨", label: "Skin", color: "#d4af37" },
    { key: "nails", icon: "💅", label: "Nails", color: "#8B4513" },
    { key: "makeup", icon: "💄", label: "Makeup", color: "#d4af37" },
  ];

  // Stylists
  const stylists = [
    {
      id: 1,
      name: "Victoria Chen",
      role: "Master Stylist",
      expertise: "Hair Design, Bridal",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80",
      bio: "With 12 years of experience in luxury salons, Victoria specializes in creating stunning hair transformations.",
      rating: 5.0,
      reviews: 247,
      available: true
    },
    {
      id: 2,
      name: "Marcus Aurelius",
      role: "Color Specialist",
      expertise: "Color, Highlights, Balayage",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80",
      bio: "Marcus is a master of color artistry, known for his precision and innovative techniques.",
      rating: 4.9,
      reviews: 213,
      available: true
    },
    {
      id: 3,
      name: "Sophia Ramirez",
      role: "Skin Expert",
      expertise: "Facials, Anti-aging, Bridal",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80",
      bio: "Sophia brings a holistic approach to skincare, combining luxury products with therapeutic techniques.",
      rating: 5.0,
      reviews: 189,
      available: false
    },
    {
      id: 4,
      name: "Aisha Kapoor",
      role: "Makeup Artist",
      expertise: "Bridal, Editorial, Airbrush",
      image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&q=80",
      bio: "Aisha creates breathtaking makeup looks for celebrities and brides, using premium products.",
      rating: 4.8,
      reviews: 156,
      available: true
    },
    {
      id: 5,
      name: "James Park",
      role: "Nail Artist",
      expertise: "Nail Art, Manicure, Pedicure",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&q=80",
      bio: "James is a creative nail artist who turns nails into miniature works of art.",
      rating: 4.9,
      reviews: 178,
      available: true
    },
  ];

  // Blog Posts
  const editorialPosts = [
    {
      id: 1,
      titleKey: "post1_title",
      title: "The Art of Hair Transformation",
      descKey: "post1_desc",
      desc: "How our master stylists create stunning hair makeovers.",
      fullContentKey: "post1_content",
      fullContent: "Hair is more than just style—it's an expression of identity. Our master stylists combine artistic vision with technical expertise to create transformations that not only look beautiful but also boost confidence and self-esteem.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d1ed3f31-7ac5-4bc8-9137-99ba11bd08bd_hIR.jpg",
      dateKey: "post1_date",
      date: "March 15, 2024",
      authorKey: "post1_author",
      author: "Victoria Chen",
      category: "Hair"
    },
    {
      id: 2,
      titleKey: "post2_title",
      title: "Skincare Rituals",
      descKey: "post2_desc",
      desc: "Elevate your skincare routine with these luxury tips.",
      fullContentKey: "post2_content",
      fullContent: "Great skin is the foundation of beauty. Our experts share their secrets to achieving radiant, healthy skin through a combination of professional treatments and at-home rituals using the finest products.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/27cc8d7e-4107-4543-9e6d-db0c99a8fd27_haldi.jpg",
      dateKey: "post2_date",
      date: "March 10, 2024",
      authorKey: "post2_author",
      author: "Sophia Ramirez",
      category: "Skincare"
    },
    {
      id: 3,
      titleKey: "post3_title",
      title: "Bridal Beauty Guide",
      descKey: "post3_desc",
      desc: "Everything you need to know for your wedding day glow.",
      fullContentKey: "post3_content",
      fullContent: "Your wedding day is one of the most photographed days of your life. Our comprehensive guide covers everything from pre-wedding skincare to the final look, ensuring you radiate beauty and confidence.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/115f35f8-35e3-4ee8-9c43-928c379079ca_bride2.jpeg",
      dateKey: "post3_date",
      date: "March 5, 2024",
      authorKey: "post3_author",
      author: "Aisha Kapoor",
      category: "Bridal"
    }
  ];

  // Treatment Packages
  const treatmentPackages = [
    {
      id: 1,
      nameKey: "package1_name",
      name: "The Royal Experience",
      priceKey: "package1_price",
      price: 15999,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/3b64aa13-b428-40e7-a7fa-09416d4e3f48_29.jpg",
      descKey: "package1_desc",
      desc: "Full day of luxury with hair, skin, and nail services",
      color: "#d4af37",
      includes: ["Hair Cut & Style", "Luxury Facial", "Manicure & Pedicure", "Gourmet Lunch"]
    },
    {
      id: 2,
      nameKey: "package2_name",
      name: "Bridal Bliss",
      priceKey: "package2_price",
      price: 19999,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/0ade75e8-cf41-4cf3-8d04-2621e95f1431_bridal.jpg",
      descKey: "package2_desc",
      desc: "Complete bridal package for the perfect wedding day",
      color: "#8B4513",
      includes: ["Bridal Hair", "Bridal Makeup", "Skin Glow Treatment", "Manicure & Pedicure"]
    },
    {
      id: 3,
      nameKey: "package3_name",
      name: "The Executive Retreat",
      priceKey: "package3_price",
      price: 8999,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/27cc8d7e-4107-4543-9e6d-db0c99a8fd27_haldi.jpg",
      descKey: "package3_desc",
      desc: "Express luxury for the busy professional",
      color: "#d4af37",
      includes: ["Quick Hair Refresher", "Power Facial", "Express Manicure"]
    },
  ];

  // Navigation items
  const navItems = [
    { key: "nav_home", label: "HOME", page: "home" },
    { key: "nav_services", label: "SERVICES", page: "services" },
    { key: "nav_hair", label: "HAIR", page: "hair" },
    { key: "nav_skin", label: "SKIN", page: "skin" },
    { key: "nav_stylists", label: "STYLISTS", page: "stylists" },
    { key: "nav_packages", label: "PACKAGES", page: "packages" },
    { key: "nav_journal", label: "JOURNAL", page: "journal" },
    { key: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Top bar messages
  const topBarMessages = [
    "💎 LUXURY SALON EXPERIENCE",
    "✦ BOOK YOUR APPOINTMENT TODAY",
    "💇‍♀️ PREMIUM SERVICES",
    "✦ 5-STAR RATED STYLISTS"
  ];

  // Floating particles (gold sparkle)
  const particles = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 2 + Math.random() * 4,
    delay: Math.random() * 5,
    duration: 4 + Math.random() * 6,
    opacity: 0.1 + Math.random() * 0.3,
  }));

  // Auto-rotate hero images
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % salonHeroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Animate products
  useEffect(() => {
    const products = p.filteredProducts || [];
    if (products.length === 0) return;
    
    setTimeout(() => setAnimationComplete(true), 300);
    
    products.forEach((_, index) => {
      setTimeout(() => {
        setVisibleProducts(prev => [...prev, index]);
      }, index * 80 + 500);
    });
  }, [p.filteredProducts]);

  // Mouse move for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 15,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);

  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openBlogModal = useCallback((post, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedPost(post);
  }, [p.previewEditMode, p.activeEditField]);

  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
  }, []);

  const openStylistModal = useCallback((stylist, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedStylist(stylist);
  }, [p.previewEditMode, p.activeEditField]);

  const closeStylistModal = useCallback(() => {
    setSelectedStylist(null);
  }, []);

  const openPackageModal = useCallback((pkg, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedTreatment(pkg);
  }, [p.previewEditMode, p.activeEditField]);

  const closePackageModal = useCallback(() => {
    setSelectedTreatment(null);
  }, []);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedService(null);
      setSelectedPost(null);
      setSelectedStylist(null);
      setSelectedTreatment(null);
    }
  }, [p.previewEditMode, p.activeEditField]);

  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "LUXE SALON",
    title: p.storeTheme?.heroTitle || "Where Beauty Meets Luxury",
    subtitle: p.storeTheme?.heroSubtitle || "Experience the pinnacle of premium salon services",
    primaryCta: p.storeTheme?.primaryCtaText || "Book Now",
    secondaryCta: p.storeTheme?.secondaryCtaText || "View Services",
  };

  const isDarkTheme = (color) => {
    if (!color) return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  };

  const isDark = isDarkTheme(T.backgroundColor);

  // Get filtered items based on category
  const getFilteredItems = (category) => {
    if (category === "all") return salonServices;
    const map = {
      "hair": "Hair",
      "skin": "Skin",
      "nails": "Nails",
      "makeup": "Makeup",
    };
    return salonServices.filter(item => item.category === map[category]);
  };

  const filteredItems = getFilteredItems(activeCategory);

  // Get card entrance animation
  const getCardEntrance = (index) => {
    const directions = [
      { x: -40, y: -15, rotate: -3 },
      { x: 40, y: -15, rotate: 3 },
      { x: -40, y: 15, rotate: -2 },
      { x: 40, y: 15, rotate: 2 },
    ];
    return directions[index % directions.length];
  };

  return (
    <div key={refreshKey} className="w-full min-h-screen bg-[#f5f0eb] overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'Playfair Display', serif" }}>
      
      {/* Floating Gold Sparkle Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full animate-float-sparkle"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: `radial-gradient(circle, ${gold}${Math.floor(40 + Math.random() * 60).toString(16)}, transparent)`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              opacity: particle.opacity,
            }}
          />
        ))}
      </div>
      
     {/* Top Bar - White Text */}
<div className="relative bg-[#2d1b0e] py-2 overflow-hidden">
  <div className="whitespace-nowrap animate-marquee">
    {topBarMessages.map((msg, idx) => (
      <span key={idx} className="inline-block mx-8 text-xs tracking-wider text-white">
        <InlineEditableTextWithNav 
          p={p} 
          field={`top_bar_message_${idx + 1}`} 
          value={msg}
          onNavigate={handleNavigateAfterEdit}
          navPage="home"
        />
      </span>
    ))}
  </div>
</div>
      
<nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-[#2d1b0e]/95 backdrop-blur-xl shadow-lg py-2" : "bg-[#2d1b0e]/90 backdrop-blur-sm py-4"}`}>
  <div className="w-full px-4 md:px-8 flex items-center justify-between">
    <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
      <StoreLogo storeInfo={p.storeInfo} border={`border-2 ${isScrolled ? "border-gold-400" : "border-gold-300"}`} />
      <span className="font-bold text-xl tracking-tight text-white">
        <InlineEditableTextWithNav 
          p={p} 
          field="businessName" 
          value={p.storeInfo?.businessName || "Luxe Salon"} 
          onNavigate={handleNavigateAfterEdit}
          navPage="home"
        />
      </span>
    </div>
    
    <button 
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!(p.previewEditMode && p.activeEditField)) {
          setMobileMenuOpen(!mobileMenuOpen);
        }
      }} 
      className="md:hidden p-2"
    >
      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    </button>
    
    <div className="hidden md:flex items-center gap-6">
      {navItems.map((item) => (
        <button
          key={item.key}
          onClick={(e) => navigateTo(item.page, e)}
          className={`text-sm font-medium transition-all duration-300 relative group ${
            activePage === item.page 
              ? "text-gold-400" 
              : "text-white hover:text-gold-300"
          }`}
        >
          <InlineEditableTextWithNav 
            p={p} 
            field={`nav_${item.page}`} 
            value={item.label}
            onNavigate={handleNavigateAfterEdit}
            navPage={item.page}
            className="text-inherit"
          />
          <span className={`absolute -bottom-1 left-0 h-0.5 bg-gold-400 transition-all duration-300 ${activePage === item.page ? "w-full" : "w-0 group-hover:w-full"}`} />
        </button>
      ))}
    </div>
    
    <div className="flex items-center gap-3">
      <button 
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!(p.previewEditMode && p.activeEditField)) {
            p.handleGoToWishlist?.();
          }
        }} 
        className="relative group"
      >
        <div className="p-2 rounded-full transition-all duration-300 hover:bg-gold-700/30">
          <FaHeart className="text-lg text-white hover:text-gold-300 transition-colors" />
        </div>
        {p.wishlistCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gold-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
            {p.wishlistCount}
          </span>
        )}
      </button>
      <CartBtn p={p} dark={true} />
      {p.userMenuElement}
    </div>
  </div>
  
  {/* Mobile Menu */}
  {mobileMenuOpen && (
    <div className="md:hidden absolute top-full left-0 right-0 bg-[#2d1b0e] shadow-lg py-4 px-4 flex flex-col gap-2 z-50 border-t border-gold-700/30">
      {navItems.map((item) => (
        <button
          key={item.key}
          onClick={(e) => navigateTo(item.page, e)}
          className={`text-sm py-2 px-3 rounded-lg text-left ${
            activePage === item.page 
              ? "bg-gold-700/30 text-gold-400" 
              : "text-white hover:text-gold-300"
          }`}
        >
          <InlineEditableTextWithNav 
            p={p} 
            field={`nav_${item.page}`} 
            value={item.label}
            onNavigate={handleNavigateAfterEdit}
            navPage={item.page}
            className="text-inherit"
          />
        </button>
      ))}
    </div>
  )}
</nav>
      
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          {/* Hero Section */}
          <section className="relative w-full h-screen min-h-[700px] overflow-hidden -mt-[60px]">
            <div className="absolute inset-0 w-full">
              {salonHeroImages.map((img, idx) => (
                <div 
                  key={idx} 
                  className={`absolute inset-0 w-full transition-opacity duration-1000 ease-in-out ${idx === activeSlide ? "opacity-100" : "opacity-0"}`}
                >
                  <img 
                    src={img} 
                    alt={`Salon ${idx + 1}`} 
                    className="w-full h-full object-cover object-center" 
                    style={{ 
                      transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px) scale(1.05)`, 
                      transition: "transform 0.3s ease-out" 
                    }} 
                  />
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-[#2d1b0e]/90 via-[#2d1b0e]/60 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#f5f0eb] via-transparent to-transparent" />
            </div>
            
            <div className="relative h-full w-full px-4 md:px-8 flex items-center">
              <div className="max-w-3xl">
                <div className="inline-block mb-6 animate-bounce-in">
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-[#2d1b0e]/40 backdrop-blur-md rounded-full border border-gold-400/30">
                    <span className="w-2 h-2 rounded-full bg-gold-400 animate-ping" />
                    <span className="text-gold-200 text-xs tracking-[0.2em] uppercase">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="heroEyebrow" 
                        value={copy.eyebrow}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </span>
                    <span className="w-2 h-2 rounded-full bg-gold-400 animate-pulse" />
                  </div>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroTitle" 
                    value={copy.title}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  >
                    {copy.title.split(" ").map((word, i) => (
                      <span key={i} className="inline-block animate-word-pop" style={{ animationDelay: `${i * 0.15}s` }}>{word}{" "}</span>
                    ))}
                  </InlineEditableTextWithNav>
                </h1>
                <p className="mt-6 text-gold-200/90 text-lg max-w-xl animate-slide-right">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroSubtitle" 
                    value={copy.subtitle}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <button 
                    onClick={(e) => navigateTo("services", e)} 
                    className="group px-8 py-4 rounded-full bg-gold-600 text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-gold-600/30 flex items-center gap-2 animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="primaryCtaText" 
                      value={copy.primaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="services"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                  <button 
                    onClick={(e) => navigateTo("stylists", e)} 
                    className="px-8 py-4 rounded-full border-2 border-gold-400/50 text-gold-200 font-semibold tracking-wide transition-all duration-300 hover:bg-gold-600/20 hover:border-gold-300 backdrop-blur-sm animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="secondaryCtaText" 
                      value={copy.secondaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="stylists"
                    />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce-slow">
              <div className="flex flex-col items-center gap-1">
                <span className="text-gold-200/60 text-[10px] tracking-wider">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL TO EXPLORE"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </span>
                <div className="w-5 h-8 border-2 border-gold-400/30 rounded-full flex justify-center">
                  <div className="w-1 h-2 bg-gold-400/60 rounded-full mt-1 animate-scroll-dot" />
                </div>
              </div>
            </div>
          </section>
          
          {/* Featured Badge */}
          <div className="relative z-10 -mt-8 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-lg border border-gold-200">
              <span className="text-gold-600 text-sm">💎</span>
              <span className="text-[#2d1b0e] text-xs font-semibold tracking-wider">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge" 
                  value="PREMIUM SALON EXPERIENCE"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <span className="text-gold-600 text-sm">💎</span>
            </div>
          </div>
          
          {/* Service Categories */}
          <section className="w-full px-4 md:px-8 py-12">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {serviceCategories.map((cat, idx) => (
                <div 
                  key={cat.key} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={(e) => {
                    if (cat.key === "all") {
                      navigateTo("services", e);
                    } else {
                      setActiveCategory(cat.key);
                      navigateTo("services", e);
                    }
                  }}
                >
                  <div 
                    className="relative overflow-hidden rounded-2xl p-4 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-white"
                  >
                    <div className="text-3xl mb-2 transition-all duration-300 group-hover:scale-110">
                      {cat.icon}
                    </div>
                    <h3 className="font-semibold text-gray-800 text-xs">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`cat_${cat.key}`} 
                        value={cat.label}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="services"
                      />
                    </h3>
                    <div className="mt-1 h-0.5 w-0 group-hover:w-full transition-all duration-500 mx-auto" style={{ backgroundColor: cat.color }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Packages Banner */}
          <section className="w-full px-4 md:px-8 py-6">
            <div className="grid md:grid-cols-3 gap-4">
              {treatmentPackages.map((pkg, idx) => (
                <div 
                  key={pkg.id} 
                  className="group cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 animate-card-fly"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                  onClick={() => openPackageModal(pkg)}
                >
                  <div className="relative h-48">
                    <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${pkg.color}dd, ${pkg.color}88)` }} />
                    <div className="absolute inset-0 p-4 flex flex-col justify-center items-center text-white text-center">
                      <h3 className="text-xl font-bold mb-2">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={pkg.nameKey} 
                          value={pkg.name}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="packages"
                        />
                      </h3>
                      <p className="text-sm opacity-90">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={pkg.descKey} 
                          value={pkg.desc}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="packages"
                        />
                      </p>
                      <div className="mt-2 text-2xl font-bold">
                        ₹<InlineEditableTextWithNav 
                          p={p} 
                          field={pkg.priceKey} 
                          value={pkg.price.toString()}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="packages"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Featured Services */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="text-center mb-10">
              <span className="text-[#8B4513] text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_badge" 
                  value="Our Services"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2d1b0e] mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_title" 
                  value="Signature Services"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-gold-600 to-gold-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {salonServices.slice(0, 4).map((service, idx) => {
                const entrance = getCardEntrance(idx);
                const isVisible = animationComplete;
                return (
                  <div 
                    key={service.id} 
                    className={`group cursor-pointer transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-[${entrance.rotate}deg]`}`}
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => setSelectedService(service)}
                  >
                    <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                      <div className="relative h-48 overflow-hidden bg-gold-50">
                        <img src={service.image} alt={service.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center transition-all duration-400 opacity-0 group-hover:opacity-100">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              p.handleAddToCart?.(service);
                            }} 
                            className="px-5 py-2.5 bg-gold-600 text-white rounded-full text-sm font-semibold transform transition-all duration-300 hover:scale-105"
                          >
                            <InlineEditableTextWithNav 
                              p={p} 
                              field="book_now_button" 
                              value="Book Now"
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            />
                          </button>
                        </div>
                        {service.badge && (
                          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: service.badgeColor }}>
                            {service.badge}
                          </div>
                        )}
                        <div className="absolute top-2 right-2 bg-[#2d1b0e] text-gold-300 text-[10px] font-bold px-2 py-0.5 rounded-full">
                          {service.duration}
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-xs text-gray-400">{service.level}</span>
                          <span className="text-xs text-gray-300">•</span>
                          <span className="text-xs text-gray-400">{service.category}</span>
                        </div>
                        <h3 className="font-semibold text-gray-800 text-sm line-clamp-1">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={service.nameKey} 
                            value={service.name}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          />
                        </h3>
                        <div className="mt-2 flex items-center justify-between">
                          <div>
                            <span className="text-lg font-bold text-[#8B4513]">₹<InlineEditableTextWithNav 
                              p={p} 
                              field={service.priceKey} 
                              value={service.price.toString()}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            /></span>
                            {service.originalPrice > service.price && (
                              <span className="text-xs text-gray-400 line-through ml-1">₹{service.originalPrice}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gold-500">⭐</span>
                            <span className="text-xs text-gray-600">{service.level}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-10">
              <button 
                onClick={(e) => navigateTo("services", e)} 
                className="px-8 py-3 rounded-full bg-[#8B4513] text-white font-semibold hover:bg-[#6b3410] transition-all duration-300 hover:scale-105"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="view_all_button" 
                  value="View All Services"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="services"
                />
              </button>
            </div>
          </section>
          
          {/* Stylists Preview */}
          <section className="w-full px-4 md:px-8 py-8 bg-[#f5f0eb]">
            <div className="text-center mb-8">
              <span className="text-[#8B4513] text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="stylists_badge" 
                  value="Meet Our Team"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="stylists"
                />
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {stylists.map((stylist) => (
                <div 
                  key={stylist.id} 
                  className="group cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white"
                  onClick={() => openStylistModal(stylist)}
                >
                  <div className="h-48 overflow-hidden">
                    <img src={stylist.image} alt={stylist.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="p-3 text-center">
                    <h4 className="font-semibold text-[#2d1b0e] text-sm">{stylist.name}</h4>
                    <p className="text-xs text-[#8B4513]">{stylist.role}</p>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <span className="text-gold-500 text-xs">⭐</span>
                      <span className="text-xs text-gray-600">{stylist.rating} ({stylist.reviews})</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      
      {/* ==================== SERVICES PAGE ==================== */}
      {activePage === "services" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-[#8B4513] text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="services_badge" 
                value="Our Services"
                onNavigate={handleNavigateAfterEdit}
                navPage="services"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b0e] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="services_title" 
                value="Premium Salon Services"
                onNavigate={handleNavigateAfterEdit}
                navPage="services"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="services_subtitle" 
                value="Experience luxury with our expert team"
                onNavigate={handleNavigateAfterEdit}
                navPage="services"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-gold-600 to-gold-400 mx-auto mt-4 rounded-full" />
          </div>
          
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {serviceCategories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat.key 
                    ? "bg-[#8B4513] text-white shadow-lg" 
                    : "bg-white text-gray-600 hover:bg-gold-50 hover:text-[#8B4513]"
                }`}
              >
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredItems.map((service) => (
              <div 
                key={service.id} 
                className="group cursor-pointer" 
                onClick={() => setSelectedService(service)}
              >
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                  <div className="relative h-48 overflow-hidden bg-gold-50">
                    <img src={service.image} alt={service.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center transition-all duration-400 opacity-0 group-hover:opacity-100">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          p.handleAddToCart?.(service);
                        }} 
                        className="px-5 py-2.5 bg-gold-600 text-white rounded-full text-sm font-semibold transform transition-all duration-300 hover:scale-105"
                      >
                        <InlineEditableTextWithNav 
                          p={p} 
                          field="book_now_service" 
                          value="Book Now"
                          onNavigate={handleNavigateAfterEdit}
                          navPage="services"
                        />
                      </button>
                    </div>
                    {service.badge && (
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: service.badgeColor }}>
                        {service.badge}
                      </div>
                    )}
                    <div className="absolute top-2 right-2 bg-[#2d1b0e] text-gold-300 text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {service.duration}
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-xs text-gray-400">{service.level}</span>
                      <span className="text-xs text-gray-300">•</span>
                      <span className="text-xs text-gray-400">{service.category}</span>
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={service.nameKey} 
                        value={service.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="services"
                      />
                    </h3>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-lg font-bold text-[#8B4513]">₹<InlineEditableTextWithNav 
                        p={p} 
                        field={service.priceKey} 
                        value={service.price.toString()}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="services"
                      /></span>
                      {service.originalPrice > service.price && (
                        <span className="text-xs text-gray-400 line-through">₹{service.originalPrice}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">💇‍♀️</div>
              <h3 className="text-xl font-semibold text-gray-600">No services found in this category</h3>
            </div>
          )}
        </section>
      )}
      
      {/* ==================== HAIR PAGE ==================== */}
      {activePage === "hair" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-[#8B4513] text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="hair_badge" 
                value="Hair Services"
                onNavigate={handleNavigateAfterEdit}
                navPage="hair"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b0e] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="hair_title" 
                value="Luxury Hair Services"
                onNavigate={handleNavigateAfterEdit}
                navPage="hair"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="hair_subtitle" 
                value="Transform your look with our expert stylists"
                onNavigate={handleNavigateAfterEdit}
                navPage="hair"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-gold-600 to-gold-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {salonServices.filter(d => d.category === "Hair").map((service) => (
              <div 
                key={service.id} 
                className="group cursor-pointer" 
                onClick={() => setSelectedService(service)}
              >
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                  <div className="relative h-48 overflow-hidden bg-gold-50">
                    <img src={service.image} alt={service.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center transition-all duration-400 opacity-0 group-hover:opacity-100">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          p.handleAddToCart?.(service);
                        }} 
                        className="px-5 py-2.5 bg-gold-600 text-white rounded-full text-sm font-semibold transform transition-all duration-300 hover:scale-105"
                      >
                        <InlineEditableTextWithNav 
                          p={p} 
                          field="book_now_hair" 
                          value="Book Now"
                          onNavigate={handleNavigateAfterEdit}
                          navPage="hair"
                        />
                      </button>
                    </div>
                    {service.badge && (
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: service.badgeColor }}>
                        {service.badge}
                      </div>
                    )}
                    <div className="absolute top-2 right-2 bg-[#2d1b0e] text-gold-300 text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {service.duration}
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-xs text-gray-400">{service.level}</span>
                      <span className="text-xs text-gray-300">•</span>
                      <span className="text-xs text-gray-400">{service.subCategory}</span>
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={service.nameKey} 
                        value={service.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="hair"
                      />
                    </h3>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-lg font-bold text-[#8B4513]">₹<InlineEditableTextWithNav 
                        p={p} 
                        field={service.priceKey} 
                        value={service.price.toString()}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="hair"
                      /></span>
                      {service.originalPrice > service.price && (
                        <span className="text-xs text-gray-400 line-through">₹{service.originalPrice}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== SKIN PAGE ==================== */}
      {activePage === "skin" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-[#8B4513] text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="skin_badge" 
                value="Skin Services"
                onNavigate={handleNavigateAfterEdit}
                navPage="skin"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b0e] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="skin_title" 
                value="Luxury Skin Care"
                onNavigate={handleNavigateAfterEdit}
                navPage="skin"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="skin_subtitle" 
                value="Reveal radiant, glowing skin with our premium treatments"
                onNavigate={handleNavigateAfterEdit}
                navPage="skin"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-gold-600 to-gold-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {salonServices.filter(d => d.category === "Skin").map((service) => (
              <div 
                key={service.id} 
                className="group cursor-pointer" 
                onClick={() => setSelectedService(service)}
              >
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                  <div className="relative h-48 overflow-hidden bg-gold-50">
                    <img src={service.image} alt={service.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center transition-all duration-400 opacity-0 group-hover:opacity-100">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          p.handleAddToCart?.(service);
                        }} 
                        className="px-5 py-2.5 bg-gold-600 text-white rounded-full text-sm font-semibold transform transition-all duration-300 hover:scale-105"
                      >
                        <InlineEditableTextWithNav 
                          p={p} 
                          field="book_now_skin" 
                          value="Book Now"
                          onNavigate={handleNavigateAfterEdit}
                          navPage="skin"
                        />
                      </button>
                    </div>
                    {service.badge && (
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: service.badgeColor }}>
                        {service.badge}
                      </div>
                    )}
                    <div className="absolute top-2 right-2 bg-[#2d1b0e] text-gold-300 text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {service.duration}
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-xs text-gray-400">{service.level}</span>
                      <span className="text-xs text-gray-300">•</span>
                      <span className="text-xs text-gray-400">{service.subCategory}</span>
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={service.nameKey} 
                        value={service.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="skin"
                      />
                    </h3>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-lg font-bold text-[#8B4513]">₹<InlineEditableTextWithNav 
                        p={p} 
                        field={service.priceKey} 
                        value={service.price.toString()}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="skin"
                      /></span>
                      {service.originalPrice > service.price && (
                        <span className="text-xs text-gray-400 line-through">₹{service.originalPrice}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== STYLISTS PAGE ==================== */}
      {activePage === "stylists" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-[#8B4513] text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="stylists_page_badge" 
                value="Our Team"
                onNavigate={handleNavigateAfterEdit}
                navPage="stylists"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b0e] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="stylists_page_title" 
                value="Meet Our Experts"
                onNavigate={handleNavigateAfterEdit}
                navPage="stylists"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="stylists_page_subtitle" 
                value="Our team of award-winning professionals"
                onNavigate={handleNavigateAfterEdit}
                navPage="stylists"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-gold-600 to-gold-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {stylists.map((stylist) => (
              <div 
                key={stylist.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onClick={() => openStylistModal(stylist)}
              >
                <div className="h-64 overflow-hidden">
                  <img src={stylist.image} alt={stylist.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-4 text-center">
                  <h3 className="text-lg font-bold text-[#2d1b0e]">{stylist.name}</h3>
                  <p className="text-[#8B4513] text-sm font-semibold">{stylist.role}</p>
                  <p className="text-xs text-gray-500 mt-1">{stylist.expertise}</p>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <span className="text-gold-500 text-sm">⭐</span>
                    <span className="text-sm text-gray-600">{stylist.rating} ({stylist.reviews} reviews)</span>
                  </div>
                  {stylist.available && (
                    <span className="inline-block mt-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                      Available
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== PACKAGES PAGE ==================== */}
      {activePage === "packages" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-[#8B4513] text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="packages_badge" 
                value="Packages"
                onNavigate={handleNavigateAfterEdit}
                navPage="packages"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b0e] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="packages_title" 
                value="Luxury Packages"
                onNavigate={handleNavigateAfterEdit}
                navPage="packages"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="packages_subtitle" 
                value="Complete beauty experiences curated for you"
                onNavigate={handleNavigateAfterEdit}
                navPage="packages"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-gold-600 to-gold-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {treatmentPackages.map((pkg) => (
              <div 
                key={pkg.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onClick={() => openPackageModal(pkg)}
              >
                <div className="relative h-56 overflow-hidden">
                  <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${pkg.color}dd, ${pkg.color}88)` }} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white">
                      <h3 className="text-2xl font-bold mb-2">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={pkg.nameKey} 
                          value={pkg.name}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="packages"
                          isInModal={true}
                        />
                      </h3>
                      <div className="text-3xl font-bold">
                        ₹<InlineEditableTextWithNav 
                          p={p} 
                          field={pkg.priceKey} 
                          value={pkg.price.toString()}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="packages"
                          isInModal={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-gray-600 text-sm mb-4">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={pkg.descKey} 
                      value={pkg.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="packages"
                    />
                  </p>
                  <div className="space-y-1">
                    {pkg.includes.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="text-gold-500">✦</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                  <button className="mt-4 w-full py-2 bg-[#8B4513] text-white rounded-full font-semibold hover:bg-[#6b3410] transition">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="book_package" 
                      value="Book Package"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="packages"
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== JOURNAL PAGE ==================== */}
      {activePage === "journal" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-[#8B4513] text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_badge" 
                value="Our Journal"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b0e] mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_title" 
                value="Beauty Stories"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_subtitle" 
                value="Insights and inspiration from our experts"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-gold-600 to-gold-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {editorialPosts.map((post) => (
              <div 
                key={post.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onClick={(e) => openBlogModal(post, e)}
              >
                <div className="h-56 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[#8B4513] font-semibold">{post.category}</span>
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.dateKey} 
                        value={post.date}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-[#2d1b0e] mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.titleKey} 
                      value={post.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.descKey} 
                      value={post.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="by_prefix" 
                        value="By"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />{' '}
                      <span className="font-semibold text-[#8B4513]">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={post.authorKey} 
                          value={post.author}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="journal"
                        />
                      </span>
                    </span>
                    <button 
                      onClick={(e) => openBlogModal(post, e)}
                      className="text-[#8B4513] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all"
                    >
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_more_button" 
                        value="Read More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                      <span>→</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className="w-full px-4 md:px-8 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#8B4513] text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_badge" 
                  value="Our Story"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2d1b0e] mt-3">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_title" 
                  value="A Legacy of Beauty"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </h2>
              <div className="w-16 h-1 bg-gold-600 mt-4 mb-6" />
              <p className="text-gray-600 leading-relaxed mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="Luxe Salon was born from a vision to create the ultimate beauty destination. Our team of award-winning professionals is dedicated to providing an unparalleled luxury experience."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="We combine the finest products, cutting-edge techniques, and a passion for beauty to deliver exceptional results. Whether you're here for a simple haircut or a complete bridal package, we treat every client like royalty."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-3xl font-bold text-[#8B4513]">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_value" 
                      value="50+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_label" 
                      value="Services"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#8B4513]">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_value" 
                      value="10+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_label" 
                      value="Expert Stylists"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#8B4513]">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_value" 
                      value="5k+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_label" 
                      value="Happy Clients"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/3b64aa13-b428-40e7-a7fa-09416d4e3f48_29.jpg" 
                alt="About Luxe Salon" 
                className="w-full h-full object-cover" 
              />
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== PRODUCT TABS ==================== */}
      <section className="w-full px-4 md:px-8 mt-8">
        <div className="flex justify-center gap-2 flex-wrap">
          {[
            ["products", "💇‍♀️ Shop Services"],
            ["services", "🛠 Services"],
            ["details", "📖 About Store"]
          ].map(([key, label]) => (
            <button 
              key={key} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.setActiveTab(key);
                }
              }} 
              className={`px-5 md:px-7 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${p.activeTab === key ? "bg-[#8B4513] text-white shadow-lg" : "text-gray-500 hover:text-[#8B4513]"}`}
            >
              <InlineEditableTextWithNav 
                p={p} 
                field={`tab_${key}_label`} 
                value={label}
                onNavigate={handleNavigateAfterEdit}
                navPage={activePage}
              />
            </button>
          ))}
        </div>
      </section>
      
      {/* Main Content */}
      <section className="flex-1 w-full px-4 md:px-8 py-8 md:py-12">
        <TabContent p={p} />
      </section>
      
      {/* Newsletter */}
      <section className="bg-gradient-to-r from-[#2d1b0e] to-[#4a2816] py-16 mt-8 w-full">
        <div className="max-w-3xl mx-auto text-center px-4">
          <span className="text-gold-300 text-sm tracking-wider uppercase">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_badge" 
              value="Subscribe"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-gold-100 mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_title" 
              value="Join the Luxe Club"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </h3>
          <p className="text-gold-200/70 text-sm mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_subtitle" 
              value="Get 20% off your first visit + exclusive beauty tips"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder={p.storefrontText?.email_placeholder || "Your email address"} 
              className="flex-1 px-5 py-3 bg-white/10 border border-gold-400/30 rounded-full text-white placeholder-gold-300/50 focus:border-gold-400 outline-none transition" 
            />
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  // Handle newsletter subscription
                }
              }}
              className="px-6 py-3 rounded-full bg-gold-600 text-white font-semibold hover:bg-gold-500 transition"
            >
              <InlineEditableTextWithNav 
                p={p} 
                field="subscribe_button" 
                value="Subscribe"
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </button>
          </div>
        </div>
      </section>
      
      <StoreFooter p={p} />
      
      {/* ==================== MODALS ==================== */}
      
      {/* Service Modal */}
      {selectedService && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <img src={selectedService.image} alt={selectedService.name} className="w-full h-full object-cover" />
              <button 
                onClick={() => setSelectedService(null)} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-gold-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              {selectedService.badge && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg" style={{ backgroundColor: selectedService.badgeColor }}>
                  {selectedService.badge}
                </div>
              )}
              <div className="absolute bottom-3 right-3 bg-[#2d1b0e] text-gold-300 text-xs font-bold px-2 py-1 rounded-full">
                {selectedService.duration}
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-400">{selectedService.level}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-400">{selectedService.category}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-400">{selectedService.subCategory}</span>
              </div>
              <h3 className="text-2xl font-bold text-[#2d1b0e]">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedService.nameKey} 
                  value={selectedService.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </h3>
              <p className="text-gray-600 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedService.descriptionKey} 
                  value={selectedService.description}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </p>
              <div className="mt-4">
                <p className="text-xs text-gray-500 font-semibold">Benefits:</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {selectedService.benefits?.map((benefit, idx) => (
                    <span key={idx} className="text-xs bg-gold-50 text-[#8B4513] px-2 py-0.5 rounded-full">
                      ✦ {benefit}
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <span className="text-3xl font-bold text-[#8B4513]">
                  ₹<InlineEditableTextWithNav 
                    p={p} 
                    field={selectedService.priceKey} 
                    value={selectedService.price.toString()}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                {selectedService.originalPrice > selectedService.price && (
                  <span className="text-sm text-gray-400 line-through">
                    ₹<InlineEditableTextWithNav 
                      p={p} 
                      field={selectedService.originalPriceKey} 
                      value={selectedService.originalPrice.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage={activePage}
                      isInModal={true}
                    />
                  </span>
                )}
              </div>
              <button 
                onClick={() => {
                  p.handleAddToCart?.(selectedService);
                  setSelectedService(null);
                }} 
                className="w-full mt-6 py-3 bg-[#8B4513] text-white rounded-full font-semibold hover:bg-[#6b3410] transition"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="book_now_modal" 
                  value="Book Now"
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Stylist Modal */}
      {selectedStylist && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="grid md:grid-cols-2">
              <div className="h-96 md:h-full overflow-hidden">
                <img src={selectedStylist.image} alt={selectedStylist.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <button 
                  onClick={closeStylistModal} 
                  className="float-right text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ✕
                </button>
                <h3 className="text-2xl font-bold text-[#2d1b0e]">{selectedStylist.name}</h3>
                <p className="text-[#8B4513] font-semibold">{selectedStylist.role}</p>
                <p className="text-sm text-gray-500 mt-1">{selectedStylist.expertise}</p>
                <p className="text-gray-600 mt-3">{selectedStylist.bio}</p>
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-gold-500">⭐</span>
                  <span className="text-sm text-gray-600">{selectedStylist.rating} ({selectedStylist.reviews} reviews)</span>
                </div>
                {selectedStylist.available && (
                  <span className="inline-block mt-3 text-xs bg-green-500 text-white px-3 py-1 rounded-full">
                    Available for booking
                  </span>
                )}
                <button 
                  onClick={() => {
                    closeStylistModal();
                    navigateTo("services");
                  }} 
                  className="mt-4 w-full py-2 bg-[#8B4513] text-white rounded-full font-semibold hover:bg-[#6b3410] transition"
                >
                  Book with {selectedStylist.name}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Package Modal */}
      {selectedTreatment && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-56">
              <img src={selectedTreatment.image} alt={selectedTreatment.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${selectedTreatment.color}dd, ${selectedTreatment.color}88)` }} />
              <button 
                onClick={closePackageModal} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-gold-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white">
                  <h3 className="text-2xl font-bold">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedTreatment.nameKey} 
                      value={selectedTreatment.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="packages"
                      isInModal={true}
                    />
                  </h3>
                  <div className="text-2xl font-bold mt-2">
                    ₹<InlineEditableTextWithNav 
                      p={p} 
                      field={selectedTreatment.priceKey} 
                      value={selectedTreatment.price.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="packages"
                      isInModal={true}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600 text-sm mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedTreatment.descKey} 
                  value={selectedTreatment.desc}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="packages"
                  isInModal={true}
                />
              </p>
              <div className="space-y-2">
                <p className="font-semibold text-[#2d1b0e] text-sm">Package Includes:</p>
                {selectedTreatment.includes.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="text-gold-500">✦</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <button 
                onClick={() => {
                  closePackageModal();
                  navigateTo("services");
                }} 
                className="mt-6 w-full py-3 bg-[#8B4513] text-white rounded-full font-semibold hover:bg-[#6b3410] transition"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="book_package_modal" 
                  value="Book This Package"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="packages"
                  isInModal={true}
                />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Blog Modal */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-[#2d1b0e]">Beauty Stories</h3>
              <button 
                onClick={closeBlogModal} 
                className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-gold-600 hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-64 object-cover rounded-xl" />
              </div>
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-gold-100 text-[#8B4513] text-xs rounded-full font-semibold">
                  {selectedPost.category}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#2d1b0e] mb-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedPost.titleKey} 
                  value={selectedPost.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                  isInModal={true}
                />
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 border-b border-gray-100 pb-4 mb-4">
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.dateKey} 
                    value={selectedPost.date}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="by_prefix" 
                    value="By"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />{' '}
                  <span className="font-semibold text-[#8B4513]">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedPost.authorKey} 
                      value={selectedPost.author}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                      isInModal={true}
                    />
                  </span>
                </span>
              </div>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.fullContentKey} 
                    value={selectedPost.fullContent}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </p>
              </div>
              <div className="mt-8 pt-4 border-t border-gray-100 flex justify-end">
                <button 
                  onClick={closeBlogModal} 
                  className="px-6 py-2 rounded-full bg-[#8B4513] text-white hover:bg-[#6b3410] transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee { animation: marquee 20s linear infinite; }
        
        @keyframes float-sparkle { 0%,100% { transform: translateY(0px) translateX(0px) scale(0.5); opacity: 0.1; } 50% { transform: translateY(-40px) translateX(20px) scale(1); opacity: 0.5; } }
        .animate-float-sparkle { animation: float-sparkle 5s ease-in-out infinite; }
        
        @keyframes word-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg); } 60% { transform: scale(1.08) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .animate-word-pop { animation: word-pop 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes slide-right { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        .animate-slide-right { animation: slide-right 0.7s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.7s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes card-fly { 0% { opacity: 0; transform: translateY(40px) scale(0.9); } 60% { transform: translateY(-5px) scale(1.02); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-card-fly { animation: card-fly 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(12px); opacity: 0; } }
        .animate-scroll-dot { animation: scroll-dot 1.5s ease-in-out infinite; }
        
        @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-8px); } }
        .animate-bounce-slow { animation: bounce 2s ease-in-out infinite; }
        
        @keyframes slide-up-modal { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up { animation: slide-up-modal 0.4s ease-out forwards; }
        
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}